(() => {
  const initializedRoots = new WeakSet();
  const focusStorageKey = "enso-cart-focus-v1";

  const readNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const getQuantityBounds = (input) => ({
    removeMin: readNumber(input.min, 0),
    ruleMin: Math.max(readNumber(input.dataset.quantityMin, 1), 1),
    max: input.max === "" ? Number.POSITIVE_INFINITY : readNumber(input.max, Number.POSITIVE_INFINITY),
    step: Math.max(readNumber(input.step, 1), 1),
  });

  const normalizedQuantity = (input) => {
    const { removeMin, ruleMin, max, step } = getQuantityBounds(input);
    const fallback = readNumber(input.dataset.lastValidQuantity, ruleMin);
    const rawValue = String(input.value).trim();
    const current = rawValue === "" ? fallback : readNumber(rawValue, fallback);

    if (current === 0 && removeMin === 0) return 0;

    const snapped = ruleMin + Math.round((current - ruleMin) / step) * step;
    return Math.min(max, Math.max(ruleMin, snapped));
  };

  const setStoredFocus = (state) => {
    try {
      window.sessionStorage.setItem(focusStorageKey, JSON.stringify(state));
    } catch (_error) {
      // Cart updates remain complete when storage is unavailable.
    }
  };

  const takeStoredFocus = () => {
    try {
      const stored = window.sessionStorage.getItem(focusStorageKey);
      window.sessionStorage.removeItem(focusStorageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (_error) {
      return null;
    }
  };

  const lineFromState = (root, state) => {
    const lines = Array.from(root.querySelectorAll("[data-enso-cart-line]"));
    if (state.key) {
      const keyedLine = lines.find((line) => line.dataset.lineKey === state.key);
      if (keyedLine) return keyedLine;
    }

    const index = Number(state.index);
    if (Number.isInteger(index) && index >= 0 && lines[index]) return lines[index];

    if (state.variantId) {
      return lines.find((line) => line.dataset.cartVariantId === state.variantId) || null;
    }

    return null;
  };

  const restoreFocus = (root) => {
    const state = takeStoredFocus();
    if (!state) return;

    window.requestAnimationFrame(() => {
      const line = lineFromState(root, state);

      if (line && state.type === "quantity") {
        const input = line.querySelector("[data-enso-cart-quantity]");
        if (input) {
          input.focus();
          return;
        }
      }

      if (line) {
        line.focus();
        return;
      }

      root.querySelector(".enso-cart__title")?.focus();
    });
  };

  const jsonEndpoint = (path) => (path.endsWith(".js") ? path : `${path}.js`);

  const initCart = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const form = root.querySelector("[data-enso-cart-form]");
    restoreFocus(root);
    if (!form) return;

    const updateButton = root.querySelector("[data-enso-cart-update]");
    const error = root.querySelector("[data-enso-cart-error]");
    const quantities = Array.from(root.querySelectorAll("[data-enso-cart-quantity]"));
    const initialQuantities = new Map(
      quantities.map((input) => {
        const quantity = normalizedQuantity(input);
        input.value = String(quantity);
        input.dataset.lastValidQuantity = String(quantity);
        return [input.dataset.lineKey, String(quantity)];
      })
    );
    let busy = false;

    root.classList.add("is-enhanced");

    const showError = (message) => {
      if (!error) return;
      error.textContent = message || root.dataset.errorMessage;
      error.hidden = false;
      error.focus();
    };

    const clearError = () => {
      if (!error) return;
      error.textContent = "";
      error.hidden = true;
    };

    const syncStepper = (input) => {
      const stepper = input.closest("[data-enso-cart-stepper]");
      if (!stepper) return;

      const value = normalizedQuantity(input);
      const { ruleMin, max } = getQuantityBounds(input);
      const decrease = stepper.querySelector('[data-enso-cart-step="decrease"]');
      const increase = stepper.querySelector('[data-enso-cart-step="increase"]');

      if (decrease) decrease.disabled = busy || value <= ruleMin;
      if (increase) increase.disabled = busy || value >= max;
    };

    const isDirty = () =>
      quantities.some(
        (input) => String(normalizedQuantity(input)) !== initialQuantities.get(input.dataset.lineKey)
      );

    const syncControls = () => {
      quantities.forEach(syncStepper);
      if (updateButton) updateButton.disabled = busy || !isDirty();
      root.querySelectorAll("[data-enso-cart-remove]").forEach((link) => {
        if (busy) link.setAttribute("aria-disabled", "true");
        else link.removeAttribute("aria-disabled");
      });
    };

    const setBusy = (nextBusy) => {
      busy = nextBusy;
      root.classList.toggle("is-loading", busy);
      form.setAttribute("aria-busy", String(busy));
      quantities.forEach((input) => {
        input.disabled = busy;
      });
      root.querySelectorAll('[type="submit"]').forEach((button) => {
        if (busy) button.disabled = true;
        else if (button !== updateButton) button.disabled = false;
      });
      syncControls();
    };

    const currentUpdates = () => {
      const updates = {};
      quantities.forEach((input) => {
        const quantity = normalizedQuantity(input);
        input.value = String(quantity);
        input.dataset.lastValidQuantity = String(quantity);
        updates[input.dataset.lineKey] = Number(input.value);
      });
      return updates;
    };

    const updateCart = async (updates, focusState) => {
      if (busy) return;

      clearError();
      setBusy(true);

      try {
        const response = await window.fetch(jsonEndpoint(root.dataset.cartUpdateUrl), {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ updates }),
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (_error) {
          payload = null;
        }

        if (!response.ok) {
          const responseMessage = payload?.description || payload?.message;
          throw new Error(responseMessage || root.dataset.errorMessage);
        }

        setStoredFocus(focusState);
        window.location.reload();
      } catch (requestError) {
        setBusy(false);
        showError(requestError instanceof Error ? requestError.message : root.dataset.errorMessage);
      }
    };

    quantities.forEach((input) => {
      syncStepper(input);

      input.addEventListener("input", () => {
        clearError();
        syncControls();
      });

      input.addEventListener("change", () => {
        const quantity = normalizedQuantity(input);
        input.value = String(quantity);
        input.dataset.lastValidQuantity = String(quantity);
        clearError();
        syncControls();
      });
    });

    root.querySelectorAll("[data-enso-cart-step]").forEach((button) => {
      button.addEventListener("click", () => {
        if (busy) return;

        const input = document.getElementById(button.dataset.inputId);
        if (!input) return;

        const { ruleMin, max, step } = getQuantityBounds(input);
        const direction = button.dataset.ensoCartStep === "increase" ? 1 : -1;
        const current = normalizedQuantity(input);
        const next = direction > 0
          ? Math.min(max, current + step)
          : Math.max(ruleMin, current - step);

        input.value = String(next);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    form.addEventListener("submit", (event) => {
      const submitter = event.submitter;
      if (submitter?.name === "checkout" || busy) return;

      event.preventDefault();
      if (!form.reportValidity()) return;

      const focusedInput = document.activeElement?.matches?.("[data-enso-cart-quantity]")
        ? document.activeElement
        : quantities.find(
            (input) => String(normalizedQuantity(input)) !== initialQuantities.get(input.dataset.lineKey)
          );

      updateCart(currentUpdates(), {
        type: "quantity",
        key: focusedInput?.dataset.lineKey || quantities[0]?.dataset.lineKey || "",
        index: focusedInput?.dataset.lineIndex || quantities[0]?.dataset.lineIndex || "",
        variantId: focusedInput?.dataset.variantId || quantities[0]?.dataset.variantId || "",
      });
    });

    root.querySelectorAll("[data-enso-cart-remove]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();
        if (busy) return;

        const updates = currentUpdates();
        updates[link.dataset.lineKey] = 0;

        updateCart(updates, {
          type: "remove",
          key: link.dataset.nextLineKey || "",
          index: link.dataset.lineIndex || "",
          variantId: link.dataset.nextVariantId || "",
        });
      });
    });

    syncControls();
  };

  const initWithin = (scope = document) => {
    if (scope.matches?.("[data-enso-cart]")) initCart(scope);
    scope.querySelectorAll?.("[data-enso-cart]").forEach(initCart);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initWithin());
  } else {
    initWithin();
  }

  document.addEventListener("shopify:section:load", (event) => initWithin(event.target));
})();
